<template>
  <main ref="supportPage" class="support-page">
    <section class="support-hero" aria-labelledby="support-title">
      <div class="support-hero__mark" aria-hidden="true">
        <img src="/static/images/support/support-hero-logo.svg" alt="">
      </div>
      <h1 id="support-title">{{ t('support.title') }}</h1>
      <p>{{ t('support.lead') }}</p>
    </section>

    <section v-if="deviceShowcaseEnabled" ref="deviceSection" class="device-section device-section-reveal" aria-labelledby="device-title">
      <div class="support-container">
        <h2 id="device-title" class="device-section__heading">{{ t('support.selectDevice') }}</h2>

        <div class="series-tabs" role="tablist" :aria-label="t('support.deviceSeries')">
          <button
            v-for="series in deviceSeries"
            :id="`series-tab-${series.id}`"
            :key="series.id"
            class="series-tab"
            :class="{ 'is-active': activeSeriesId === series.id }"
            type="button"
            role="tab"
            :aria-selected="activeSeriesId === series.id"
            :aria-controls="`series-panel-${series.id}`"
            @click="selectSeries(series.id)"
          >
            {{ series.label }}
          </button>
        </div>

        <div
          v-if="currentSeries"
          :id="`series-panel-${currentSeries.id}`"
          class="device-grid device-section__cards"
          role="tabpanel"
          :aria-labelledby="`series-tab-${currentSeries.id}`"
        >
          <article
            v-for="device in currentSeries.devices"
            :key="device.id"
            class="device-card"
            :class="`device-card--${device.id}`"
          >
            <div class="device-card__visual">
              <img
                v-if="device.coverUrl"
                :src="device.coverUrl"
                :alt="`${device.name} device`"
                loading="lazy"
                decoding="async"
              >
              <span v-else class="device-card__placeholder" aria-hidden="true">{{ device.name }}</span>
            </div>
            <h3>{{ device.name }}</h3>
          </article>
        </div>
      </div>
    </section>

    <SupportFaq ref="faqSection" class="support-faq-reveal" :categories="faqCategories" id-prefix="support-home-faq" />
  </main>
</template>

<script>
import SupportFaq from '@/components/SupportFaq.vue'
import { supportAPI } from '@/utils/api'

const {
  normalizeSupportDeviceSeriesResponse,
  normalizeSupportFaqResponse
} = require('@/utils/support-data.cjs')
const { siteLanguageMixin } = require('@/utils/site-language.js')
const { createLatestSiteRequestGuard } = require('@/utils/site-request.cjs')

export default {
  name: 'FAQ',
  components: {
    SupportFaq
  },
  mixins: [siteLanguageMixin],
  data() {
    return {
      // Phase 1: re-enable here to restore both the model section and its request.
      deviceShowcaseEnabled: false,
      deviceSeries: [],
      faqCategories: [],
      activeSeriesId: null,
      supportRequestGuard: createLatestSiteRequestGuard(),
      pageRevealObserver: null
    }
  },
  computed: {
    currentSeries() {
      return this.deviceSeries.find((series) => series.id === this.activeSeriesId) || this.deviceSeries[0] || null
    }
  },
  watch: {
    currentLanguage() {
      this.loadSupportData()
    }
  },
  async mounted() {
    document.title = 'Support & FAQ - ANOVATOR'
    await this.loadSupportData()
    this.$nextTick(this.initPageReveal)
  },
  beforeUnmount() {
    if (this.pageRevealObserver) this.pageRevealObserver.disconnect()
  },
  methods: {
    getRevealElement(reference) {
      return reference && (reference.$el || reference)
    },
    initPageReveal() {
      const page = this.$refs.supportPage
      if (!page || window.matchMedia('(prefers-reduced-motion: reduce)').matches || typeof window.IntersectionObserver !== 'function') return
      const targets = [this.$refs.deviceSection, this.getRevealElement(this.$refs.faqSection)].filter(Boolean)
      if (!targets.length || this.pageRevealObserver) return

      try {
        this.pageRevealObserver = new window.IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) return
            entry.target.classList.add('is-revealed')
            this.pageRevealObserver.unobserve(entry.target)
          })
        }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' })
        targets.forEach((target) => this.pageRevealObserver.observe(target))
        page.classList.add('reveal-enabled')
      } catch (error) {
        if (this.pageRevealObserver) this.pageRevealObserver.disconnect()
        this.pageRevealObserver = null
      }
    },
    selectSeries(id) {
      this.activeSeriesId = id
    },
    async loadSupportData() {
      const request = this.supportRequestGuard.start(this.currentLanguage)
      try {
        const [devices, faqs] = await Promise.all([
          this.deviceShowcaseEnabled ? supportAPI.getDevices(request.siteType) : null,
          supportAPI.getFaqs(request.siteType)
        ])
        if (!this.supportRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.deviceSeries = normalizeSupportDeviceSeriesResponse(devices)
        this.faqCategories = normalizeSupportFaqResponse(faqs)
        if (!this.deviceSeries.some((series) => series.id === this.activeSeriesId)) {
          this.activeSeriesId = this.deviceSeries.length ? this.deviceSeries[0].id : null
        }
      } catch (error) {
        if (!this.supportRequestGuard.isCurrent(request, this.currentLanguage)) return
        this.deviceSeries = []
        this.faqCategories = []
        console.error('Failed to load support data:', error)
      }
    }
  }
}
</script>

<style scoped>
.support-page {
  --support-accent: #ef0a6a;
  min-width: 0;
  padding-top: var(--global-header-height, 44px);
  overflow: hidden;
  color: #000;
  background: #fff;
}

.support-container {
  width: min(1296px, calc(100% - 48px));
  margin: 0 auto;
}

.support-hero {
  height: 442px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 80px;
  text-align: center;
  background: #fff;
  box-sizing: border-box;
}

.support-hero__mark {
  width: 120px;
  height: 120px;
  display: grid;
  place-items: center;
  overflow: hidden;
  margin-bottom: 29px;
  border-radius: 50%;
  background: var(--support-accent);
}

.support-hero__mark img {
  width: 60px;
  height: auto;
  object-fit: contain;
  transform: none;
}

.support-hero h1,
.device-section h2,
.device-card h3,
.support-hero p {
  margin: 0;
}

.support-hero h1 {
  font-size: 50px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
}

.support-hero p {
  margin-top: 24px;
  font-size: 28px;
  line-height: 1.25;
  color: rgba(0, 0, 0, 0.8);
}

.device-section {
  min-height: 612px;
  padding: 80px 0 79px;
  background: #fff;
  box-sizing: border-box;
}

.device-section h2 {
  font-size: 40px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
}

.series-tabs {
  height: 65px;
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 160px;
  margin-top: 32px;
  border-bottom: 1px solid #ccc;
}

.series-tab {
  font: inherit;
}

.series-tab {
  position: relative;
  min-width: 112px;
  padding: 15px 0 16px;
  border: 0;
  color: #000;
  background: transparent;
  font-size: 26px;
  line-height: 32px;
  cursor: pointer;
}

.series-tab::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 4px;
  background: transparent;
}

.series-tab.is-active::after {
  background: #000;
}

.series-tab.is-active {
  color: #000;
}

.series-tab:focus {
  outline: none;
}

.series-tab:focus-visible {
  outline: 2px solid var(--support-accent);
  outline-offset: 3px;
}

.device-grid {
  display: grid;
  grid-template-columns: repeat(5, 240px);
  gap: 24px;
  margin-top: 39px;
}

.device-card {
  min-width: 0;
  display: block;
  color: inherit;
  text-align: center;
  text-decoration: none;
  cursor: default;
  transition: transform 200ms ease;
}

.device-card__visual {
  position: relative;
  width: 240px;
  height: 240px;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: 12px;
  background: #f5f5f7;
  isolation: isolate;
  transition: box-shadow 200ms ease;
}

.device-card__visual img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform: scale(1.18);
  mix-blend-mode: multiply;
}

.device-card--m5 .device-card__visual img {
  transform: scale(1.3);
}

.device-card--m3 .device-card__visual img {
  transform: scale(1.37);
}

.device-card--m1 .device-card__visual img {
  transform: scale(1.54);
}

.device-card__placeholder {
  display: grid;
  width: 96px;
  height: 160px;
  place-items: center;
  border-radius: 12px 12px 28px 28px;
  color: rgba(0, 0, 0, 0.42);
  background: linear-gradient(180deg, #dedee2, #fff);
  font-size: 24px;
  font-weight: 700;
}

.device-card h3 {
  margin-top: 10px;
  font-size: 18px;
  font-weight: 700;
  line-height: 18px;
}

@media (min-width: 701px) {
  .support-page.reveal-enabled .device-section__heading,
  .support-page.reveal-enabled .device-section-reveal .device-card,
  .support-page.reveal-enabled .support-faq-reveal {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 620ms ease-out, transform 620ms ease-out;
  }

  .support-page.reveal-enabled .device-section-reveal:not(.is-revealed) .device-section__heading,
  .support-page.reveal-enabled .device-section-reveal:not(.is-revealed) .device-card,
  .support-page.reveal-enabled .support-faq-reveal:not(.is-revealed) {
    opacity: 0;
    transform: translateY(30px);
  }

  .support-page.reveal-enabled .device-section-reveal .device-card:nth-child(2) {
    transition-delay: 80ms;
  }

  .support-page.reveal-enabled .device-section-reveal .device-card:nth-child(n + 3) {
    transition-delay: 160ms;
  }

  .support-page .device-section-reveal .device-card:hover {
    transform: translateY(-4px);
  }

  .device-card:hover .device-card__visual {
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  }
}

@media (max-width: 1100px) {
  .device-grid {
    grid-template-columns: repeat(3, minmax(0, 240px));
    justify-content: center;
  }

  .device-section {
    padding-bottom: 80px;
  }
}

@media (max-width: 700px) {
  .support-page.reveal-enabled .device-section-reveal,
  .support-page.reveal-enabled .support-faq-reveal {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 560ms ease-out, transform 560ms ease-out;
  }

  .support-page.reveal-enabled .device-section-reveal:not(.is-revealed),
  .support-page.reveal-enabled .support-faq-reveal:not(.is-revealed) {
    opacity: 0;
    transform: translateY(20px);
  }

  .support-container {
    width: min(400px, calc(100vw - 32px));
    margin: 0 0 0 16px;
  }

  .support-hero {
    height: 599px;
    padding-top: 60px;
  }

  .support-hero__mark {
    margin-bottom: 29px;
  }

  .support-hero h1 {
    width: 280px;
    font-size: 40px;
    line-height: 1.2;
    white-space: normal;
  }

  .support-hero p {
    margin-top: 70px;
    font-size: 28px;
  }

  .device-section {
    height: 527px;
    min-height: 0;
    padding: 56px 0 0;
  }

  .device-section h2 {
    width: 281px;
    margin: 0 auto;
    font-size: 40px;
    line-height: 1.2;
  }

  .series-tabs {
    height: 65px;
    justify-content: center;
    gap: 40px;
    margin-top: 66px;
  }

  .series-tab {
    min-width: auto;
    flex: 0 0 auto;
    padding: 15px 0 16px;
    font-size: 26px;
    line-height: 32px;
  }

  .device-grid {
    display: flex;
    width: 100%;
    grid-template-columns: none;
    justify-content: flex-start;
    gap: 15px;
    margin-top: 27px;
    padding-bottom: 4px;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
  }

  .device-grid::-webkit-scrollbar {
    display: none;
  }

  .device-card {
    width: 144px;
    min-width: 144px;
    flex: 0 0 144px;
  }

  .device-card__visual {
    width: 144px;
    height: 144px;
    aspect-ratio: auto;
  }

  .device-card h3 {
    margin-top: 2px;
    font-size: 14px;
    line-height: 16px;
  }
}

@media (max-width: 390px) {
  .support-container {
    width: min(400px, calc(100vw - 32px));
  }
}

@media (prefers-reduced-motion: reduce) {
  .support-page.reveal-enabled .device-section-reveal,
  .support-page.reveal-enabled .device-section__heading,
  .support-page.reveal-enabled .device-section-reveal .device-card,
  .support-page.reveal-enabled .support-faq-reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
